// Menu loading/cook overlay — extracted verbatim from MenuScene.ts (FACE&FURY S4 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were rewritten
// to `export function name(this: MenuScene, …)` and bound back onto the scene as class fields.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from '../keys';
import type { BoardFetchPhase } from '../../net/types';
import { audio } from '../audio';
import { motionAllowed, scaledFont } from '../settings';
import { headingText, chip, labelText, cardPanel } from '../ui/kit';
import { icon, drawIcon } from '../icons';
import { attachMascot } from '../mascot';
import type { MenuScene } from '../scenes/MenuScene';

function drawCookBackdrop(scene: Phaser.Scene, layer: Phaser.GameObjects.Container, W: number, H: number, cx: number): void {
    const g = scene.add.graphics();
    const grid = Math.max(54, Math.min(104, W * 0.09));
    g.lineStyle(1, PALETTE.line, 0.16);
    for (let x = cx % grid; x < W; x += grid) g.lineBetween(x, 0, x, H);
    for (let y = H % grid; y < H; y += grid) g.lineBetween(0, y, W, y);

    const frameW = Math.min(W * 0.86, 620);
    const frameH = Math.min(H * 0.82, 660);
    const frameY = H * 0.5;
    g.lineStyle(1.25, PALETTE.scan, 0.16);
    g.strokeRoundedRect(cx - frameW / 2, frameY - frameH / 2, frameW, frameH, 18);
    const c = 28;
    const l = frameW / 2;
    const t = frameH / 2;
    g.lineStyle(2, PALETTE.scan, 0.32);
    g.lineBetween(cx - l, frameY - t + c, cx - l, frameY - t);
    g.lineBetween(cx - l, frameY - t, cx - l + c, frameY - t);
    g.lineBetween(cx + l - c, frameY - t, cx + l, frameY - t);
    g.lineBetween(cx + l, frameY - t, cx + l, frameY - t + c);
    g.lineBetween(cx - l, frameY + t - c, cx - l, frameY + t);
    g.lineBetween(cx - l, frameY + t, cx - l + c, frameY + t);
    g.lineBetween(cx + l - c, frameY + t, cx + l, frameY + t);
    g.lineBetween(cx + l, frameY + t - c, cx + l, frameY + t);
    layer.add(g);
  }

  // --- loading state ---------------------------------------------------------
export function beginLoading(this: MenuScene, label: string): void {
    void label; // heading is fixed ("BUILDING YOUR BOARD"); the topic comes from launchingLabel.
    this.loading = true;
    // Announcer hypes the topic while the verified board builds (throttled internally).
    audio().playVo('voTopicIntro');
    if (this.inputEl) this.inputEl.style.display = 'none';

    const W = this.scale.width;
    const H = this.scale.height;
    const cx = W / 2;

    // Opaque branded "cook" beat — the signature "a second AI verifies every answer" trust moment.
    // Fully covers the menu (incl. the depth-30k top-row chrome) so the overlay reads as its own screen.
    const veil = this.add.rectangle(cx, H / 2, W, H, PALETTE.void, 1).setInteractive();
    const layer = this.add.container(0, 0).setDepth(20_000);
    drawCookBackdrop(this, layer, W, H, cx);

    // Unified raster mascot (bobbing if motion) with a soft glow. bob:false — the cook overlay runs
    // its own tighter float+tilt tween below, so the helper's default bob would double-drive y.
    const heroY = H * 0.27;
    const glow = this.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    for (let i = 3; i >= 1; i--) {
      glow.fillStyle(PALETTE.player, 0.05 * i);
      glow.fillCircle(cx, heroY, 26 * i);
    }
    const m = attachMascot(this, 'player', cx, heroY, 84, { bob: false });
    layer.add([glow, m.obj]);
    if (motionAllowed()) {
      this.loadingTween = this.tweens.add({
        targets: m.obj,
        y: { from: heroY - 5, to: heroY + 5 },
        angle: { from: -4, to: 4 },
        duration: 480,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }

    // Heading + topic pill.
    layer.add(chip(this, cx, H * 0.385, 'Verified board cook', { icon: 'shield', textColor: PALETTE.correct, borderColor: PALETTE.correct, fill: PALETTE.surface, originX: 0.5, fontPx: 11 }));
    layer.add(headingText(this, cx, H * 0.43, 'BUILDING YOUR BOARD', { size: Math.max(20, Math.min(30, W * 0.05)) }));
    const rawTopic = (this.launchingLabel || (this.launchingDaily ? "Today's Gauntlet" : 'Your topic')).trim();
    const topic = rawTopic.length > 24 ? `${rawTopic.slice(0, 22)}…` : rawTopic;
    layer.add(chip(this, cx, H * 0.49, topic, { icon: 'flask', textColor: PALETTE.scan, borderColor: PALETTE.scan, fill: PALETTE.surface, originX: 0.5 }));

    // Indeterminate cook bar — a contiguous scan fill breathes across the track. It never renders as
    // detached segments in a still; the live checklist carries the real phase state.
    const barY = H * 0.545;
    const trackW = Math.min(W * 0.7, 320);
    const track = this.add.rectangle(cx, barY, trackW, 6, PALETTE.tile, 1).setStrokeStyle(1, PALETTE.lineStrong, 0.6);
    const shimmer = this.add.rectangle(cx - trackW / 2, barY, trackW, 6, PALETTE.scan, 0.95).setOrigin(0, 0.5).setScale(0.58, 1);
    if (!motionAllowed()) {
      shimmer.setScale(0.62, 1);
    } else {
      this.tweens.add({ targets: shimmer, scaleX: { from: 0.38, to: 0.88 }, duration: 780, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' });
    }
    layer.add([track, shimmer]);

    // Verify-pipeline checklist (matches the handoff): an icon-state list on the bare field — green
    // checkCircle = done, spinning cyan circleNotch = active, dim circleNotch = pending — NOT filled
    // rows (those read as progress bars and lose the done/active/pending shape double-coding).
    // The rows are LIVE: they advance on real boardClient phases (accepted → generating/polling →
    // verified → local build) via onCookPhase/finishCooking, never on a fake timer.
    const rowW = Math.min(W * 0.82, 440);
    const stepX = cx - rowW / 2 + 10;
    const stepLabels = ['Writing questions', 'Fact-checking every answer', 'Arming the Gauntlet'];
    const stepGap = Math.max(40, Math.min(50, H * 0.05));
    const stepY0 = H * 0.6;
    const stepYs = [stepY0, stepY0 + stepGap, stepY0 + stepGap * 2];
    const frameTop = stepYs[0] - 38;
    const frameBottom = stepYs[2] + 38;
    const stepFrame = cardPanel(this, cx, (frameTop + frameBottom) / 2, rowW + 42, frameBottom - frameTop, {
      fill: PALETTE.surface,
      border: PALETTE.line,
      radius: 14,
      accent: PALETTE.scan,
    });
    stepFrame.add(labelText(this, -rowW / 2 + 20, -((frameBottom - frameTop) / 2) + 18, 'VERIFY PIPELINE', { size: 10, color: PALETTE.scan }));
    layer.add(stepFrame);
    this.cookSteps = [];
    // Gate the first repaint to >=150ms after open so an instant cache hit still reads as a cascade.
    this.cookNextAt = this.time.now + 150;
    stepLabels.forEach((stepLabel, i) => {
      const ic = icon(this, 'circleNotch', stepX + 12, stepYs[i], 22, PALETTE.mute);
      const txt = this.add
        .text(stepX + 36, stepYs[i], stepLabel, { fontFamily: FONT.family, fontSize: `${scaledFont(14, 12)}px`, color: hex(PALETTE.mute), fontStyle: '600' })
        .setOrigin(0, 0.5);
      layer.add([ic, txt]);
      this.cookSteps.push({ icon: ic, label: txt, state: 'pending' });
    });
    // A real launch (inflight request) starts truthfully at "writing" until the backend accepts it.
    // The devBridge 'board-cook' capture hook opens this overlay with NO request in flight — freeze
    // it at the reference mid-cook snapshot (done / active / pending) so parity captures still match.
    const live = this.inflight !== undefined;
    this.cookPhase = live ? 'request' : 'accepted';
    this.setCookStep(0, live ? 'active' : 'done');
    if (!live) this.setCookStep(1, 'active');

    // Trust line (shield + the second-AI promise).
    const trustY = H * 0.93;
    const trustTxt = this.add
      .text(0, trustY, 'A second AI verifies every answer before you see it', { fontFamily: FONT.family, fontSize: `${scaledFont(Math.max(11, W * 0.018), 11)}px`, color: hex(PALETTE.text) })
      .setOrigin(0, 0.5);
    const shS = 15;
    const trustW = shS + 6 + trustTxt.width;
    const tsx = cx - trustW / 2;
    const shield = icon(this, 'shield', tsx + shS / 2, trustY, shS, PALETTE.correct);
    trustTxt.setX(tsx + shS + 6);
    layer.add([shield, trustTxt]);

    veil.setDepth(19_999);
    // Above the depth-30k top-row chrome (rank pill / missions pill / icon cluster) so they're hidden.
    this.loadingLayer = this.add.container(0, 0, [veil, layer]).setDepth(40_000);
    this.loadingDotsEvent = undefined;
  }

  /** Repaint one checklist row: green check = done, cyan notch (spins only if motion) = active,
   *  dim notch = pending. Steps advance discretely, so reduced-motion still reads the state change. */
export function setCookStep(this: MenuScene, i: number, state: 'pending' | 'active' | 'done', label?: string): void {
    const s = this.cookSteps[i];
    if (!s || !s.icon.active) return;
    if (label) s.label.setText(label);
    if (s.state === state && !label) return;
    s.state = state;
    s.spin?.remove();
    s.spin = undefined;
    s.icon.setAngle(0);
    s.icon.clear();
    const color = state === 'done' ? PALETTE.correct : state === 'active' ? PALETTE.scan : PALETTE.mute;
    drawIcon(s.icon, state === 'done' ? 'checkCircle' : 'circleNotch', 22, color);
    s.label.setColor(hex(state === 'pending' ? PALETTE.dim : PALETTE.text));
    if (state === 'active' && motionAllowed()) {
      s.spin = this.tweens.add({ targets: s.icon, angle: { from: 0, to: 360 }, duration: 900, repeat: -1, ease: 'Linear' });
    }
  }

  /** Schedule a checklist repaint so consecutive advances land >=150ms apart: slow real phases paint
   *  the moment they occur; an instant cache hit becomes a fast VISIBLE cascade — never multi-second
   *  theater, never a skip-straight-to-done blink. */
export function advanceCook(this: MenuScene, fn: () => void): void {
    const now = this.time.now;
    const at = Math.max(now, this.cookNextAt);
    this.cookNextAt = at + 150;
    if (at <= now) {
      if (this.loading) fn();
    } else {
      this.time.delayedCall(at - now, () => {
        if (this.loading) fn();
      });
    }
  }

  /** Live net-layer phases → checklist rows (deduped; 202 polls re-report 'generating' harmlessly). */
export function onCookPhase(this: MenuScene, phase: BoardFetchPhase): void {
    if (!this.loading) return;
    this.markCookAccepted();
    if (phase !== 'verified' || this.cookPhase === 'verified') return;
    this.cookPhase = 'verified';
    this.advanceCook(() => {
      this.setCookStep(1, 'done');
      this.setCookStep(2, 'active');
    });
  }

  /** Step 1 done + step 2 active, exactly once (the backend accepted the request / began cooking). */
export function markCookAccepted(this: MenuScene): void {
    if (this.cookPhase !== 'request') return;
    this.cookPhase = 'accepted';
    this.advanceCook(() => {
      this.setCookStep(0, 'done');
      this.setCookStep(1, 'active');
    });
  }

  /**
   * A board is here: advance whatever rows the live phases never reached, run the local-build step,
   * then close the overlay and hand off. A seed-pack board RELABELS the verify row — the pack is
   * pre-verified at build time (boardSchema contract) — so the checklist never claims a live
   * fact-check that didn't happen.
   */
export function finishCooking(this: MenuScene, origin: 'generated' | 'seed', onClosed: () => void): void {
    if (!this.loadingLayer || this.cookSteps.length === 0) {
      this.endLoading();
      onClosed();
      return;
    }
    this.markCookAccepted();
    if (this.cookPhase !== 'verified') {
      this.cookPhase = 'verified';
      this.advanceCook(() => {
        this.setCookStep(1, 'done', origin === 'seed' ? 'Pre-verified classic board' : undefined);
        this.setCookStep(2, 'active');
      });
    } else if (origin === 'seed') {
      // Defensive: 'verified' should never fire for a seed fallback, but keep the row honest anyway.
      this.advanceCook(() => this.setCookStep(1, 'done', 'Pre-verified classic board'));
    }
    this.advanceCook(() => this.setCookStep(2, 'done'));
    // Close just after the last repaint lands (cookNextAt is the timeline head).
    this.time.delayedCall(Math.max(0, this.cookNextAt - this.time.now), () => {
      if (!this.scene.isActive()) return;
      this.endLoading();
      onClosed();
    });
  }

export function endLoading(this: MenuScene): void {
    this.loading = false;
    this.loadingTween?.remove();
    this.loadingTween = undefined;
    this.loadingDotsEvent?.remove();
    this.loadingDotsEvent = undefined;
    for (const s of this.cookSteps) s.spin?.remove();
    this.cookSteps = [];
    this.loadingLayer?.destroy();
    this.loadingLayer = undefined;
    if (this.inputEl) this.inputEl.style.display = 'block';
  }
